import { Link } from 'react-router-dom';
import { Car, CreditCard, Wrench, Settings } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useSubscription } from '../../hooks/useSubscription';
import { PageHeader, Card, CardHeader, Badge } from '../common';

export default function CarOwnerDashboard() {
  const { user } = useAuth();
  const {
    subscription,
    subscriptionLoading,
    canAccessVehicleProfiles,
    getTierDisplayName
  } = useSubscription();

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title={`Welcome, ${user?.full_name || 'User'}`}
        subtitle="Manage your vehicles, maintenance, and account."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* My Vehicles */}
        {canAccessVehicleProfiles && (
          <Link to="/vehicle-profiles" className="block group">
            <Card className="h-full transition-colors duration-state group-hover:border-border-strong">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-lg bg-surface-sunken">
                  <Car className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
                </div>
                <h3 className="ml-3 text-card-title text-ink-soft">My Vehicles</h3>
              </div>
              <p className="text-body text-ink-muted mb-4">
                Track maintenance, health scores, and get personalized recommendations.
              </p>
              <span className="text-body-strong text-accent group-hover:text-accent-hover transition-colors">
                Manage Vehicles →
              </span>
            </Card>
          </Link>
        )}

        {/* Billing */}
        <Link to="/billing" className="block group">
          <Card className="h-full transition-colors duration-state group-hover:border-border-strong">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-lg bg-surface-sunken">
                <CreditCard className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="ml-3 text-card-title text-ink-soft">Billing</h3>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-body text-ink-muted">Plan:</span>
                <span className="text-body-strong text-ink-soft">
                  {subscriptionLoading ? '...' : getTierDisplayName()}
                </span>
              </div>
              {subscription && (
                <div className="flex justify-between items-center">
                  <span className="text-body text-ink-muted">Status:</span>
                  <Badge status={subscription.status === 'active' ? 'success' : 'danger'}>
                    {subscription.status}
                  </Badge>
                </div>
              )}
            </div>
            <span className="text-body-strong text-accent group-hover:text-accent-hover transition-colors">
              Manage Subscription →
            </span>
          </Card>
        </Link>

        {/* Profile */}
        <Link to="/profile" className="block group">
          <Card className="h-full transition-colors duration-state group-hover:border-border-strong">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-lg bg-surface-sunken">
                <Settings className="h-5 w-5 text-ink-soft" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <h3 className="ml-3 text-card-title text-ink-soft">Profile & Settings</h3>
            </div>
            <p className="text-body text-ink-muted mb-4">
              Manage your account, preferences, and notification settings.
            </p>
            <span className="text-body-strong text-accent group-hover:text-accent-hover transition-colors">
              View Profile →
            </span>
          </Card>
        </Link>
      </div>
    </div>
  );
}
